import { Injectable, computed, inject, signal } from '@angular/core';

import { AuthService } from '../../authentication/_services/auth.service';
import { type Currency } from '../master-data/currencies/currency.model';

import { type ChapterSummaryFM } from './chapter.model';
import { ChapterService } from './chapter.service';

/**
 * The logged-in member's chapter context — its summary (name, number, district) and currency.
 *
 * **Keyed on the chapter id (September 2026).** Logging out doesn't reload the app, so a
 * once-per-session cache would carry one chapter's name and currency into the next officer's
 * session when a second chapter's officer signs in on the same tab. {@link load} now re-fetches
 * whenever the logged-in chapter differs from the one cached, and clears the old values first.
 *
 * Every amount in RMBF is denominated in the chapter's currency (base TDD §9), so
 * any screen showing a *current* amount (an event fee, a badge threshold) needs it.
 * Screens showing a *snapshotted* amount don't: a TYFCB, RSVP or visitor fee carries
 * its own currency in the payload, which is the whole point of the snapshot.
 *
 * Fetched once per session from `GET /chapter/{id}/summary` — the smallest payload that
 * carries the currency, and the same endpoint the Flutter `chapterCurrencyProvider` uses,
 * so there is one explanation rather than two. (`GET /chapter/{id}` would also work here:
 * every chapter screen in this app is ClientAdmin-only. (The ApplicationSupport login has no chapter, so
 * {@link load} is a no-op for it.) That endpoint's
 * `@PreAuthorize` is what rules it out on *mobile*, not here — it is a heavier payload,
 * not a forbidden one.)
 *
 * Cached here rather than re-fetched per screen, and re-fetched only when {@link refresh}
 * is called (the Chapter Settings screen does, after a change, so an admin doesn't keep
 * seeing the old symbol until the next login). Note the dashboard already calls
 * `getSummary` for its own header — worth feeding this service from there rather than
 * fetching twice, once a second screen needs the currency.
 */
@Injectable({
  providedIn: 'root'
})
export class ChapterContextService {
  private chapterService = inject(ChapterService);
  private authService = inject(AuthService);

  private readonly currencySignal = signal<Currency | null>(null);

  private readonly summarySignal = signal<ChapterSummaryFM | null>(null);

  /** The logged-in chapter's summary, or null until it loads — the toolbar shows a chapter-neutral title meanwhile. */
  readonly summary = this.summarySignal.asReadonly();

  /** Null until the first load resolves — callers pass it straight to `SharedService.formatMoney`, which renders the number bare in that window. */
  readonly currency = this.currencySignal.asReadonly();

  /** The chapter the cached values belong to; a different logged-in chapter triggers a fresh load. */
  private loadedFor: string | null = null;

  /** Idempotent per chapter: safe to call from every screen's constructor; fetches once per logged-in chapter. */
  load(): void {
    const chapterId = this.authService.currentChapterId;
    if (!chapterId || chapterId === this.loadedFor) {
      return;
    }
    this.loadedFor = chapterId;
    this.summarySignal.set(null);
    this.currencySignal.set(null);
    this.refresh();
  }

  /** Re-fetches unconditionally. Call after changing the chapter's currency. */
  refresh(): void {
    const chapterId = this.authService.currentChapterId;
    if (!chapterId) {
      return;
    }
    this.chapterService.getSummary(chapterId).subscribe({
      next: summary => {
        this.summarySignal.set(summary);
        this.currencySignal.set(summary.currency ?? null);
      },
      // A missing currency is not worth a toast: formatMoney renders the amount bare,
      // which is the intended degraded state, not an error the member can act on.
      error: () => this.currencySignal.set(null)
    });
  }

  /**
   * What to put in front of an amount input: the symbol, else the code, else nothing —
   * the same rule as `SharedService.formatMoney` and Flutter's `Currency.prefix`. An
   * empty prefix renders a bare field rather than a guessed symbol.
   */
  readonly prefix = computed(() => {
    const currency = this.currencySignal();
    return (currency?.symbol || currency?.code || '').trim();
  });

  /** Lets the Chapter Settings screen publish what it just saved without a second round trip. */
  set(currency: Currency | null): void {
    this.loadedFor = this.authService.currentChapterId;
    this.currencySignal.set(currency);
  }
}
